import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CPLAT_API_ROUTES, useFetch } from '@/api';
import { useAppSelector } from '@/redux/store';
import type { CplatApiResponse } from '@/types/api';

import { getIsAnySubscribed, resolveMobileMembershipCplatToken } from './mobile-membership-auth';
import {
  createFallbackMobileMembershipPolicy,
  MOBILE_MEMBERSHIP_CHANNEL,
  resolveMobileMembershipPolicy,
  type MobileMembershipPolicy,
} from './mobile-membership-utils';
import {
  getMobileMembershipMockPolicy,
  getShouldUseMobileMembershipMock,
} from './mobile-membership-mock';

type UseMobileMembershipPolicyOptions = {
  enableMock?: boolean;
};

export const useMobileMembershipPolicy = (
  channel = MOBILE_MEMBERSHIP_CHANNEL,
  options?: UseMobileMembershipPolicyOptions,
) => {
  const { CplatToken, ServiceInfo } = useAppSelector((state) => state.user);
  const [searchParams] = useSearchParams();
  const cplatToken = resolveMobileMembershipCplatToken(CplatToken, searchParams.get('cplatToken'));
  const isMockEnabled = (options?.enableMock ?? true) && getShouldUseMobileMembershipMock();
  type MobileMembershipQuoteResponse = CplatApiResponse<
    MobileMembershipPolicy | MobileMembershipPolicy[]
  >;
  const fallbackPolicy = useMemo(
    () => createFallbackMobileMembershipPolicy(getIsAnySubscribed(ServiceInfo)),
    [ServiceInfo],
  );
  const fallbackPolicyResponse = useMemo<MobileMembershipQuoteResponse>(
    () => ({
      code: 'FALLBACK',
      message: 'fallback',
      data: fallbackPolicy,
    }),
    [fallbackPolicy],
  );
  const params = useMemo(
    () => ({
      cplatToken,
      channel,
    }),
    [channel, cplatToken],
  );

  const policyQuery = useFetch<MobileMembershipQuoteResponse>(CPLAT_API_ROUTES.getMembershipQuote, {
    method: 'POST',
    params,
    config: {
      enabled: !isMockEnabled,
      retry: false,
      staleTime: 30 * 1000,
      gcTime: 5 * 60 * 1000,
      placeholderData: fallbackPolicyResponse,
      throwOnError: false,
    },
  });

  const policy = useMemo(
    () => resolveMobileMembershipPolicy(policyQuery.data, fallbackPolicy),
    [fallbackPolicy, policyQuery.data],
  );
  const isPolicyError =
    !isMockEnabled &&
    !policyQuery.isFetching &&
    (policyQuery.isError || (policyQuery.data !== undefined && policyQuery.data.code !== '200'));

  if (isMockEnabled) {
    return {
      policy: getMobileMembershipMockPolicy(),
      isPolicyLoading: false,
      isPolicyError: false,
      isFallbackPolicy: false,
    };
  }

  return {
    policy,
    isPolicyLoading: policyQuery.isLoading || policyQuery.isFetching,
    isPolicyError,
    isFallbackPolicy: policy === fallbackPolicy,
  };
};
